import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  booleanAttribute,
  computed,
  contentChildren,
  inject,
  input,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { map } from 'rxjs';

import { SvgIconComponent, VerticalPosDirective } from '@dmb/core';

import { LayoutTranslation } from '../layout-translation';
import { MenuService } from '../menu.service';
import { SubmenuItemComponent } from '../submenu-item/submenu-item.component';

@Component({
  selector: 'dmb-top-level',
  templateUrl: './top-level.component.html',
  styleUrls: ['./top-level.component.scss'],
  imports: [RouterLinkActive, VerticalPosDirective, NgTemplateOutlet, RouterLink, SvgIconComponent],
  host: {
    '[class.warn]': 'warn()',
    '[class.activated]': 'classActivated()',
    '[class.disabled]': '!enabled()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TopLevelComponent {
  private tr = LayoutTranslation.getTranslations();
  private ar = inject(Router);
  private currentUrl = toSignal(this.ar.events.pipe(map(() => this.ar.url)), { initialValue: this.ar.url });
  private menuService = inject(MenuService);

  key = input.required<keyof LayoutTranslation>();
  label = input<string>();
  path = input<string>();
  icon = input<string>();
  alwaysEnabled = input(false, { transform: booleanAttribute });
  warn = input(false);
  custom = input(false);
  expanded = input(true); // for the search bar
  activate = input<() => void>();

  submenu = contentChildren(SubmenuItemComponent);

  activeChild = computed(() => {
    const url = this.currentUrl();
    return this.submenu().some((s) => `/${s.path()}` === url);
  });

  classActivated = computed(() => this.menuService.active() === this.key());

  enabled = computed(() => {
    if (this.alwaysEnabled()) {
      return true;
    }

    return this.menuService.menu()?.includes(this.key()) ?? false;
  });

  internalLabel = computed(() => {
    const l = this.label();
    if (l !== undefined) {
      return l;
    }

    return this.tr[this.key()];
  });

  async toggleActivated($event?: Event) {
    $event?.preventDefault();

    const callback = this.activate();
    if (callback) {
      callback();
      return;
    }

    const k = this.key();
    this.menuService.active.update((a) => (a === k ? null : k));
    $event?.stopPropagation();
  }

  deactivate() {
    this.menuService.setInactive();
  }
}
